/**
 * Save Indicator Component
 *
 * Visual feedback for auto-save status:
 * - "Saving..." with spinner while saving
 * - "Saved" with checkmark when done
 * - Fades out after 2 seconds
 * - Shows error message if save fails
 */

import { useEffect, useState } from 'react';
import './SaveIndicator.css';

export interface SaveIndicatorProps {
  /**
   * Is currently saving
   */
  isSaving: boolean;

  /**
   * Last successful save time
   */
  lastSaved: Date | null;

  /**
   * Error message from last failed save
   */
  error?: string | null;

  /**
   * How long to show "Saved" message (ms)
   */
  showDuration?: number;
}

export default function SaveIndicator({
  isSaving,
  lastSaved,
  error,
  showDuration = 2000,
}: SaveIndicatorProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [status, setStatus] = useState<'saving' | 'saved' | 'error'>('saved');

  /**
   * Update visibility and status
   */
  useEffect(() => {
    if (isSaving) {
      setStatus('saving');
      setIsVisible(true);
    } else if (error) {
      setStatus('error');
      setIsVisible(true);
    } else if (lastSaved) {
      setStatus('saved');
      setIsVisible(true);

      // Hide after duration
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, showDuration);

      return () => clearTimeout(timer);
    }
  }, [isSaving, lastSaved, error, showDuration]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`save-indicator ${status} ${isVisible ? 'visible' : 'hidden'}`}>
      <div className="save-indicator-content">
        {status === 'saving' && (
          <>
            <div className="spinner" />
            <span className="save-indicator-text">Saving...</span>
          </>
        )}

        {status === 'saved' && (
          <>
            <span className="checkmark">✓</span>
            <span className="save-indicator-text">Saved</span>
            {lastSaved && (
              <span className="save-time">
                {lastSaved.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </span>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <span className="error-icon">⚠</span>
            <div className="error-content">
              <span className="save-indicator-text">Failed to save</span>
              {error && <span className="error-message">{error}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
